import {
	Button,
	HStack,
	IconLayers,
	Input,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import {
	ACCOUNT_API_COMPANIES,
	SYSTEM_ENTITIES_UPDATE_ENTITY,
} from 'apiRoutes';
import { CompanySelect } from 'components/companies/CompanySelect';
import { validateCompanyName } from 'components/companies/companyValidation';
import { Company } from 'domain/company/Company';
import { SystemEntity } from 'domain/systemEntity/interfaces/SystemEntity';
import { useGetOnMount } from 'hooks/useGetOnMount';
import useSubmit, { POST } from 'hooks/useSubmit';
import { FunctionComponent, useState } from 'react';
import { styled } from 'styled-components';

const EditableSystemEntityDetails: FunctionComponent<{
	systemEntity: SystemEntity;
	setIsEditing: (isEditing: boolean) => void;
	onSuccessfulSubmit: () => Promise<void>;
}> = ({ systemEntity, setIsEditing, onSuccessfulSubmit }) => {
	const [formState, setFormState] = useState<{
		id: string;
		name: string;
		companyId: number;
	}>({
		id: systemEntity.id,
		name: systemEntity.displayName,
		companyId: systemEntity.companyId || 0,
	});

	const [errors, setErrors] = useState<{
		name?: string;
	}>({});

	const { data: companies } = useGetOnMount<Company[]>(
		`${ACCOUNT_API_COMPANIES}/getcompanies`,
	);

	const [postSystemEntity] = useSubmit(
		`${SYSTEM_ENTITIES_UPDATE_ENTITY}`,
		POST,
		{
			messageOnError: 'An error occured, please try again!',
			messageOnSuccess: `System entity ${formState.name} was successfully updated.`,
			showApiError: true,
		},
	);

	return (
		<VStack gap="xl" fullWidth>
			<StyledHStack>
				<Input
					type="text"
					label="Name"
					name="name"
					value={formState.name}
					required
					onChange={(event) =>
						setFormState({
							...formState,
							name: event.target.value,
						})
					}
					onBlur={() =>
						setErrors({
							...errors,
							name: validateCompanyName(systemEntity.displayName),
						})
					}
					errorMessage={errors.name}
				/>
			</StyledHStack>
			<StyledHStack>
				<CompanySelect
					companies={companies}
					value={formState.companyId}
					onChange={(companyId) =>
						setFormState({
							...formState,
							companyId: companyId,
						})
					}
				/>
			</StyledHStack>
			<HStack wrap="nowrap" gap="md" fullWidth justify="flex-end">
				<Button
					text={'Cancel'}
					variant="secondary"
					onClick={() => {
						setIsEditing(false);
					}}
				/>
				<Button
					text={'Submit'}
					onClick={async () => {
						const response = await postSystemEntity(formState);

						if (response.success) {
							await onSuccessfulSubmit();
							setIsEditing(false);
						}
					}}
				/>
			</HStack>
		</VStack>
	);
};

const StyledHStack = styled(HStack)`
	width: 50%;
`;

export default EditableSystemEntityDetails;
